import componentStyles from './snipedOddsCard.module.css';
import globalStyles from '../globals.module.css';
import React, { useState, useEffect } from 'react';
import { FaCheck, FaPlus } from 'react-icons/fa';

const SnipedOddsCard = ({
    snipedOdd,
    useContextConfidence = false,
    isExpanded = false,
    onToggle,
    onViewMore,
    loadingAllOpportunities = false,
    isAuthenticated = false,
    isSaved = false,
    onToggleSaved
}) => {
    // State for selected comparison line (default to pinnacle point)
    const [selectedLine, setSelectedLine] = useState(null);
    const [selectedOpportunityKey, setSelectedOpportunityKey] = useState(null);

    // New API contract provides UI-friendly fields under `display`.
    // Keep fallbacks to legacy fields so older payload variants still render.
    const display = snipedOdd?.display || {};
    const hasDisplayOdds = display && (display.odds !== undefined || display.pinnacle_odds !== undefined);

    // Helper function to render signed numbers (e.g. +5.0, -3.0)
    const formatSignedNumber = (value, decimals = 1) => {
        if (value === null || value === undefined || isNaN(value)) return 'N/A';
        const num = Number(value);
        const fixed = num.toFixed(decimals);
        return num > 0 ? `+${fixed}` : fixed;
    };

    // Use edge (line_advantage) as a simple confidence signal.
    // Thresholds can be tuned later; these are intentionally conservative defaults.
    const getConfidenceFromEdge = (edge) => {
        if (edge === null || edge === undefined || isNaN(edge)) return null;
        const absEdge = Math.abs(Number(edge));
        if (absEdge >= 5) return { label: 'HIGH', color: 'var(--bloomberg-green)' };
        if (absEdge >= 3) return { label: 'MED', color: 'var(--bloomberg-yellow)' };
        return { label: 'LOW', color: 'var(--bloomberg-orange)' };
    };

    const getConfidenceFromContextScore = (score) => {
        const value = Number(score);
        if (!Number.isFinite(value)) return null;

        if (value >= 7) return { label: 'HIGH', color: 'var(--bloomberg-green)' };
        if (value >= 5) return { label: 'MED', color: 'var(--bloomberg-yellow)' };
        return { label: 'LOW', color: 'var(--bloomberg-orange)' };
    };

    // Helper function to format American odds
    const formatAmericanOdds = (odds) => {
        if (odds === null || odds === undefined || isNaN(odds)) return 'N/A';
        if (odds >= 2.0) {
            // Positive odds: (decimalOdds - 1) * 100
            return `+${Math.round((odds - 1) * 100)}`;
        } else if (odds > 1.0 && odds < 2.0) {
            // Negative odds: -100 / (decimalOdds - 1)
            return `${Math.round(-100 / (odds - 1))}`;
        } else {
            return 'N/A';
        }
    };

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                hour: 'numeric', 
                minute: '2-digit' 
            });
        } catch (error) {
            return 'N/A';
        }
    };

    // Get recommendation badge color based on tier
    const getRecommendationColor = (recommendation) => {
        switch (recommendation) {
            case 'Strong':
                return '#00ff00'; // Green
            case 'Good':
                return '#90EE90'; // Light Green
            case 'Decent':
                return '#FFD700'; // Gold
            case 'Consider':
                return '#FFA500'; // Orange
            default:
                return '#808080'; // Gray
        }
    };

    // Get recommendation badge background color
    const getRecommendationBgColor = (recommendation) => {
        switch (recommendation) {
            case 'Strong':
                return 'rgba(0, 255, 0, 0.1)';
            case 'Good':
                return 'rgba(144, 238, 144, 0.1)';
            case 'Decent':
                return 'rgba(255, 215, 0, 0.1)';
            case 'Consider':
                return 'rgba(255, 165, 0, 0.1)';
            default:
                return 'rgba(128, 128, 128, 0.1)';
        }
    };

    // Format value score as percentage
    const formatValueScore = (score) => {
        if (score === null || score === undefined) return 'N/A';
        return `${(score * 100).toFixed(1)}%`;
    };
    
    // Helper function to format a hit rate that may be provided as 0-1 or 0-100.
    const formatPercent = (value) => {
        if (value === null || value === undefined || isNaN(value)) return 'N/A';
        const num = Number(value);
        if (num >= 0 && num <= 1) return `${(num * 100).toFixed(1)}%`;
        return `${num.toFixed(1)}%`;
    };
    
    // Format decimal odds to American odds
    const formatDecimalOdds = (odds) => {
        if (odds === null || odds === undefined) return 'N/A';
        if (odds >= 2.0) {
            return `+${Math.round((odds - 1) * 100)}`;
        } else {
            return Math.round(-100 / (odds - 1)).toString();
        }
    };

    // Format display odds if the API already provided a string.
    // Falls back to simple type heuristics for number values.
    const formatDisplayOdds = (odds) => {
        if (odds === null || odds === undefined) return 'N/A';
        return formatAmericanOdds(odds);
        // if (typeof odds === 'string') return odds;
        // if (typeof odds === 'number') {
            // If the backend provided a `display` object, treat odds as already UI-friendly.
            // Example payloads use decimal odds (e.g. 1.86), so render as decimal.
            // if (hasDisplayOdds) return odds.toFixed(2);
            // Heuristic for legacy payloads.
            // if (odds >= 1 && odds < 20) return formatDecimalOdds(odds);
            // return formatAmericanOdds(odds);
        // }
        // return String(odds);
    };

    // Get market display name
    const getMarketDisplayName = (marketKey) => {
        if (!marketKey) return 'N/A';
        return marketKey.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    const getEventDescription = () => {
        const awayTeam = snipedOdd.away_team || snipedOdd.awayTeam || '';
        const homeTeam = snipedOdd.home_team || snipedOdd.homeTeam || '';
        const matchup = awayTeam && homeTeam
            ? `${awayTeam} @ ${homeTeam}`
            : (snipedOdd.event_name || snipedOdd.eventName || '');
        const eventTime = formatDate(snipedOdd.commence_time || snipedOdd.commenceTime || snipedOdd.date_time || snipedOdd.dateTime);

        if (matchup && eventTime !== 'N/A') return `${matchup} / ${eventTime}`;
        if (matchup) return matchup;
        if (eventTime !== 'N/A') return eventTime;
        return null;
    };

    // Get confidence badge color
    const getConfidenceColor = (confidence) => {
        switch (confidence?.toLowerCase()) {
            case 'high':
                return '#00ff00'; // Green
            case 'medium':
                return '#FFD700'; // Gold
            case 'low':
                return '#FFA500'; // Orange
            default:
                return '#808080'; // Gray
        }
    };
    
    // Map new API structure (best, pinnacle, all_opportunities)
    const best = snipedOdd.best || {};
    const pinnacle = snipedOdd.pinnacle || {};
    const allOpportunities = snipedOdd.all_opportunities || [];
    const opportunityCount = snipedOdd.opportunity_count || 0;
    
    // Get recommendation from best object or fallback
    const recommendation = best.recommendation || snipedOdd.recommendation || null;
    const valueScore = best.value_score || 0;
    const dfsValueScore = best.dfs_value_score;
    const isDfs = best.is_dfs || false;
    const hitRates = snipedOdd.hit_rates || null;
    const contextScore = snipedOdd.contextScore || null;

    const getOpportunityKey = (opportunity, index) => {
        const bookmaker = opportunity?.bookmaker || 'unknown-book';
        const point = opportunity?.point ?? 'no-point';
        const odds = opportunity?.odds ?? 'no-odds';
        return `${bookmaker}_${point}_${odds}_${index}`;
    };
    
    // Initialize selected line to pinnacle point when component mounts or data changes
    useEffect(() => {
        const initialLineCandidate =
            (typeof display.pinnacle_line === 'number' ? display.pinnacle_line : null) ??
            (typeof pinnacle?.point === 'number' ? pinnacle.point : null) ??
            (typeof display.line === 'number' ? display.line : null) ??
            (typeof best?.point === 'number' ? best.point : null);

        if (initialLineCandidate !== null && selectedLine === null) {
            setSelectedLine(initialLineCandidate);
        }
    }, [display.pinnacle_line, display.line, pinnacle, best, selectedLine]);
    
    // Determine how to display metrics.
    // New API provides `primary_metric_label` (e.g. "Under Hit Rate") and `primary_metric`.
    // Prefer that instead of inferring "overs vs unders" from `hit_rates`, since both can be present.
    const primaryMetricLabel = snipedOdd.primary_metric_label || null;
    const primaryMetric = snipedOdd.primary_metric !== null && snipedOdd.primary_metric !== undefined ? snipedOdd.primary_metric : null;
    const explanation = snipedOdd.explanation || null;

    const isHitRatePrimary = typeof primaryMetricLabel === 'string' && primaryMetricLabel.toLowerCase().includes('hit rate');
    const isPriceType = !isHitRatePrimary;
    
    // Get direction from snipedOdd
    const direction = snipedOdd.direction || 'Over';
    const isOverDirection = String(direction).toLowerCase() === 'over';
    const isUnderDirection = String(direction).toLowerCase() === 'under';
    
    // Check if stat hit or missed based on direction and line
    const checkHit = (statValue, line) => {
        if (direction === 'Over') {
            return statValue >= line;
        } else {
            return statValue <= line;
        }
    };
    
    // Helper function to format date (YYYY-MM-DD to short format)
    const formatGameDate = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric'
            });
        } catch (error) {
            return null;
        }
    };

    // Render trend dots for historical stats (replaces bar chart)
    const renderTrendDots = () => {
        if (!hitRates || !hitRates.stats || !Array.isArray(hitRates.stats) || hitRates.stats.length === 0) {
            return null;
        }
        
        if (selectedLine === null) {
            return null;
        }
        
        // Stats are now objects with {value: number, game_date?: string}
        // Get last 10 games (or all if fewer than 10)
        // Most recent is first in array (index 0)
        const allStats = hitRates.stats;
        const lastGames = allStats.slice(0, Math.min(10, allStats.length));
        const gameCount = lastGames.length;
        
        // Determine dot color based on stat value vs selected line
        const getDotColor = (statValue, line) => {
            if (statValue > line) {
                return 'over'; // Bloomberg green
            } else if (statValue < line) {
                return 'under'; // Bloomberg red
            } else {
                return 'push'; // Bloomberg yellow
            }
        };
        
        return (
            <div className={componentStyles.trendDotsContainer}>
                <div className={componentStyles.trendDotsLabel}>
                    Last {gameCount} {gameCount === 1 ? 'Game' : 'Games'}
                </div>
                <div className={componentStyles.trendDotsRow}>
                    {lastGames.map((game, index) => {
                        const dotType = getDotColor(game.value, selectedLine);
                        return (
                            <div 
                                key={index}
                                className={`${componentStyles.trendDot} ${componentStyles[`trendDot${dotType.charAt(0).toUpperCase() + dotType.slice(1)}`]}`}
                            />
                        );
                    })}
                </div>
            </div>
        );
    };

    // Render bar chart for historical stats (COMMENTED OUT - replaced by trend dots)
    // const renderBarChart = () => {
    //     if (!hitRates || !hitRates.stats || !Array.isArray(hitRates.stats) || hitRates.stats.length === 0) {
    //         return null;
    //     }
    //     
    //     if (selectedLine === null) {
    //         return null;
    //     }
    //     
    //     // Stats are now objects with {value: number, game_date?: string}
    //     const stats = hitRates.stats;
    //     
    //     // Extract values for min/max calculations
    //     const statValues = stats.map(stat => stat.value);
    //     const maxValue = Math.max(...statValues, selectedLine);
    //     const minValue = Math.min(...statValues, selectedLine);
    //     const range = maxValue - minValue || 1;
    //     const chartHeight = 80; // Fixed height in pixels
    //     
    //     return (
    //         <div className={componentStyles.barChartContainer}>
    //             <div className={componentStyles.barChartHeader}>
    //                 <span className={componentStyles.barChartTitle}>
    //                     Historical Performance vs Line {selectedLine}
    //                 </span>
    //                 <div className={componentStyles.barChartLegend}>
    //                     <span className={componentStyles.legendItem}>
    //                         <span className={componentStyles.legendColorGreen}></span>
    //                         Hit
    //                     </span>
    //                     <span className={componentStyles.legendItem}>
    //                         <span className={componentStyles.legendColorRed}></span>
    //                         Miss
    //                     </span>
    //                 </div>
    //             </div>
    //             <div className={componentStyles.barChart} style={{ height: `${chartHeight}px` }}>
    //                 {/* Reference line */}
    //                 {selectedLine >= minValue && selectedLine <= maxValue && (
    //                     <div 
    //                         className={componentStyles.barChartReferenceLine}
    //                         style={{ 
    //                             left: `${((selectedLine - minValue) / range) * 100}%`,
    //                             height: `${chartHeight}px`
    //                         }}
    //                         title={`Line: ${selectedLine}`}
    //                     ></div>
    //                 )}
    //                 {stats.map((stat, index) => {
    //                     const statValue = stat.value;
    //                     const isHit = checkHit(statValue, selectedLine);
    //                     const barHeight = ((statValue - minValue) / range) * chartHeight;
    //                     const isLatest = index === 0; // First in array is latest
    //                     const isOldest = index === stats.length - 1; // Last in array is oldest
    //                     const formattedDate = formatGameDate(stat.game_date);
    //                     
    //                     // Build tooltip with date if available
    //                     const tooltipText = formattedDate 
    //                         ? `Game ${index + 1}: ${statValue} (${formattedDate})`
    //                         : `Game ${index + 1}: ${statValue}`;
    //                     
    //                     return (
    //                         <div key={index} className={componentStyles.barChartBarContainer}>
    //                             <div className={componentStyles.barChartBarWrapper}>
    //                                 <div 
    //                                     className={`${componentStyles.barChartBar} ${isHit ? componentStyles.barChartBarHit : componentStyles.barChartBarMiss}`}
    //                                     style={{ height: `${Math.max(barHeight, 4)}px` }}
    //                                     title={tooltipText}
    //                                 >
    //                                     <span className={componentStyles.barChartBarLabel}>
    //                                         {statValue}
    //                                     </span>
    //                                 </div>
    //                             </div>
    //                             {(isLatest || isOldest) && (
    //                                 <div className={componentStyles.barChartXLabel}>
    //                                     {isLatest ? 'Latest' : 'Oldest'}
    //                                     {formattedDate && (
    //                                         <span className={componentStyles.barChartDateLabel}>
    //                                             {formattedDate}
    //                                         </span>
    //                                     )}
    //                                 </div>
    //                             )}
    //                         </div>
    //                     );
    //                 })}
    //             </div>
    //         </div>
    //     );
    // };
    
    // Get best bookmaker name (with fallback to old structure)
    const bestBookmaker = display.bookmaker || best?.bookmaker || snipedOdd.other_bookmaker || 'N/A';
    const bestPoint = display.line ?? (best?.point !== null && best?.point !== undefined ? best.point : (snipedOdd.other_point !== null && snipedOdd.other_point !== undefined ? snipedOdd.other_point : 'N/A'));
    const bestOdds = display.odds ?? (best?.odds !== null && best?.odds !== undefined ? formatDecimalOdds(best.odds) : (snipedOdd.other_odds !== null && snipedOdd.other_odds !== undefined ? formatAmericanOdds(snipedOdd.other_odds) : 'N/A'));

    const pinnaclePoint = display.pinnacle_line ?? (pinnacle?.point !== null && pinnacle?.point !== undefined ? pinnacle.point : (snipedOdd.pinnacle_point !== null && snipedOdd.pinnacle_point !== undefined ? snipedOdd.pinnacle_point : 'N/A'));
    const pinnacleOdds = display.pinnacle_odds ?? (pinnacle?.odds !== null && pinnacle?.odds !== undefined ? formatDecimalOdds(pinnacle.odds) : (snipedOdd.pinnacle_odds !== null && snipedOdd.pinnacle_odds !== undefined ? formatAmericanOdds(snipedOdd.pinnacle_odds) : 'N/A'));

    const lineAdvantage = display.line_advantage ?? (best?.line_advantage !== null && best?.line_advantage !== undefined ? best.line_advantage : null);
    const contextConfidence = useContextConfidence && contextScore?.scoreStatus === 'scored'
        ? getConfidenceFromContextScore(contextScore.finalConfidenceScore)
        : null;
    const displayConfidence = contextConfidence || getConfidenceFromEdge(lineAdvantage);
    const displayConfidenceTitle = contextConfidence
        ? 'Context confidence'
        : `Edge ${formatSignedNumber(lineAdvantage, 1)}`;

    // Prefer the API-provided prop display name when available.
    const marketDisplayName = display.prop_display_name || getMarketDisplayName(snipedOdd.market_key);
    const eventDescription = getEventDescription();

    const formatPrimaryMetric = () => {
        if (primaryMetric === null || primaryMetric === undefined) return 'N/A';
        if (typeof primaryMetric !== 'number') return String(primaryMetric);

        const label = (primaryMetricLabel || '').toLowerCase();
        if (label.includes('hit rate')) return formatPercent(primaryMetric);
        if (label.includes('value')) return formatValueScore(primaryMetric);
        return String(primaryMetric);
    };

    const getDisplayedHitRate = () => {
        // Prefer display.* (already in percentage points, e.g. 100).
        if (isUnderDirection && display.under_hit_rate !== null && display.under_hit_rate !== undefined) return display.under_hit_rate;
        if (isOverDirection && display.over_hit_rate !== null && display.over_hit_rate !== undefined) return display.over_hit_rate;

        // Fallback to hit_rates.* (API may provide both over & under).
        if (isUnderDirection && hitRates?.under_percentage !== null && hitRates?.under_percentage !== undefined) return hitRates.under_percentage;
        if (isOverDirection && hitRates?.over_percentage !== null && hitRates?.over_percentage !== undefined) return hitRates.over_percentage;

        // Last resort: if primary metric is a hit rate, use it.
        if (isHitRatePrimary && typeof primaryMetric === 'number') return primaryMetric;
        return null;
    };

    // Helper to get recent game stats used for trend dots (max 10).
    const getRecentGames = () => {
        if (!hitRates || !hitRates.stats || !Array.isArray(hitRates.stats) || hitRates.stats.length === 0) return [];
        return hitRates.stats.slice(0, Math.min(10, hitRates.stats.length));
    };

    return (
        <div className={componentStyles.card + ' ' + globalStyles.slight3dEffect}>
            {/* Compact Header Section */}
            <div className={componentStyles.cardHeader}>
                <div className={componentStyles.headerTopRow}>
                    <div className={componentStyles.headerTop}>
                        <div className={componentStyles.playerName}>
                            {snipedOdd.player_name || 'N/A'}
                        </div>
                        <div className={componentStyles.propRow}>
                            <span>{marketDisplayName}</span>{' '}
                            <span className={isUnderDirection ? componentStyles.directionUnder : componentStyles.directionOver}>
                                {snipedOdd.direction || ''}
                            </span>
                        </div>
                        {eventDescription && (
                            <div className={componentStyles.eventInfo}>
                                {eventDescription}
                            </div>
                        )}
                    </div>
                    {isAuthenticated && (
                        <button
                            type="button"
                            className={`${componentStyles.cartToggleButton} ${isSaved ? componentStyles.cartToggleButtonSaved : ''}`}
                            onClick={(event) => {
                                event.stopPropagation();
                                if (onToggleSaved) onToggleSaved();
                            }}
                            title={isSaved ? 'Remove from bet cart' : 'Add to bet cart'}
                        >
                            {isSaved ? <FaCheck /> : <FaPlus />}
                        </button>
                    )}
                </div>
            </div>

            {/* Best vs Pinnacle comparison panel (compact, scannable) */}
            <div className={componentStyles.comparePanel}>
                {displayConfidence && (
                    <div
                        className={componentStyles.compareConfidenceBadge}
                        style={{
                            color: displayConfidence.color,
                            borderColor: displayConfidence.color
                        }}
                        title={displayConfidenceTitle}
                    >
                        CONF {displayConfidence.label}
                    </div>
                )}

                <div className={componentStyles.compareGrid}>
                    <div className={`${componentStyles.compareCol} ${componentStyles.compareColBest}`}>
                        <div className={componentStyles.compareColLabel}>Best</div>
                        <div className={componentStyles.compareBook}>{bestBookmaker}</div>
                        <div className={componentStyles.compareNumbers}>
                            <span className={componentStyles.lineValue}>{bestPoint}</span>
                            <span className={componentStyles.oddsValue}>({formatDisplayOdds(bestOdds)})</span>
                        </div>
                    </div>

                    <div className={`${componentStyles.compareCol} ${componentStyles.compareColPinnacle}`}>
                        <div className={componentStyles.compareColLabel}>Pinnacle</div>
                        <div className={componentStyles.compareBookMuted}>Pinnacle</div>
                        <div className={componentStyles.compareNumbers}>
                            <span className={componentStyles.lineValue}>{pinnaclePoint}</span>
                            <span className={componentStyles.oddsValue}>({formatDisplayOdds(pinnacleOdds)})</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Primary metric row (single “hero” metric; avoids duplication) */}
            <div className={componentStyles.metricRow}>
                <div className={componentStyles.metricLabel}>
                    {isPriceType ? 'Value Score' : (primaryMetricLabel || 'Hit Rate')}
                </div>
                <div className={componentStyles.metricValue}>
                    {isPriceType ? formatValueScore(valueScore) : (
                        (() => {
                            const hitRate = getDisplayedHitRate();
                            return hitRate !== null && hitRate !== undefined ? formatPercent(hitRate) : formatPrimaryMetric();
                        })()
                    )}
                </div>
                <div className={componentStyles.metricMeta}>
                    {(display.games_analyzed > 0 || hitRates?.games_analyzed > 0) ? `(${display.games_analyzed || hitRates.games_analyzed} games)` : ''}
                </div>
            </div>

            {/* Trend dots section (keep current over/under/push semantics; add clarity) */}
            {(() => {
                const recentGames = getRecentGames();
                if (!recentGames.length || selectedLine === null) return null;
                return (
                    <div className={componentStyles.trendSection}>
                        <div className={componentStyles.trendHeader}>
                            <div className={componentStyles.trendTitle}>
                                Last {recentGames.length} vs {selectedLine}
                            </div>
                            <div className={componentStyles.trendLegend}>
                                <span className={componentStyles.legendItem}>
                                    <span className={`${componentStyles.legendDot} ${componentStyles.legendDotOver}`} />
                                    Over
                                </span>
                                <span className={componentStyles.legendItem}>
                                    <span className={`${componentStyles.legendDot} ${componentStyles.legendDotUnder}`} />
                                    Under
                                </span>
                                <span className={componentStyles.legendItem}>
                                    <span className={`${componentStyles.legendDot} ${componentStyles.legendDotPush}`} />
                                    Push
                                </span>
                            </div>
                        </div>
                        {renderTrendDots()}
                    </div>
                );
            })()}
            
            {/* View More Button */}
            {opportunityCount > 1 && (
                <div className={componentStyles.viewMoreContainer}>
                    {!isExpanded ? (
                        <button
                            type="button"
                            className={componentStyles.viewMoreButton}
                            onClick={onViewMore}
                            disabled={loadingAllOpportunities}
                        >
                            {loadingAllOpportunities ? (
                                <span>Loading...</span>
                            ) : (
                                <span>View More ({opportunityCount} books) →</span>
                            )}
                        </button>
                    ) : (
                        <button
                            type="button"
                            className={componentStyles.viewMoreButton}
                            onClick={onToggle}
                        >
                            <span>View Less ↑</span>
                        </button>
                    )}
                </div>
            )}
            
            {/* All Opportunities Table (when expanded) */}
            {isExpanded && (
                <div className={componentStyles.opportunitiesTableContainer}>
                    {/* Trend dots moved to hit rate line above */}
                    {/* {(isOversType || isUndersType) && hitRates && hitRates.stats && hitRates.stats.length > 0 && renderTrendDots()} */}
                    {/* Bar chart replaced with trend dots */}
                    {/* {(isOversType || isUndersType) && hitRates && hitRates.stats && hitRates.stats.length > 0 && renderBarChart()} */}
                    
                    {loadingAllOpportunities ? (
                        <div className={componentStyles.tableLoading}>
                            Loading all opportunities...
            </div>
                    ) : allOpportunities && allOpportunities.length > 0 ? (
                        <table className={componentStyles.opportunitiesTable}>
                            <thead>
                                <tr>
                                    <th>Bookmaker</th>
                                    <th>Line</th>
                                    <th>Odds</th>
                                    <th>Value</th>
                                    <th>Advantage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allOpportunities.map((opp, index) => {
                                    const isBest = index === 0 || (opp.value_score === best.value_score && opp.bookmaker === best.bookmaker);
                                    const oppLine = opp.point !== null && opp.point !== undefined ? opp.point : null;
                                    const opportunityKey = getOpportunityKey(opp, index);
                                    const isSelected = selectedOpportunityKey === opportunityKey;
                                    
                                    return (
                                        <tr 
                                            key={index} 
                                            className={`${isBest ? componentStyles.bestOpportunityRow : ''} ${isSelected ? componentStyles.selectedOpportunityRow : ''}`}
                                            onClick={() => {
                                                setSelectedOpportunityKey(opportunityKey);
                                                if (oppLine !== null) {
                                                    setSelectedLine(oppLine);
                                                }
                                            }}
                                            style={{ cursor: oppLine !== null ? 'pointer' : 'default' }}
                                        >
                                            <td>{opp.bookmaker || 'N/A'}</td>
                                            <td>{oppLine !== null ? oppLine : 'N/A'}</td>
                                            <td>
                                                {opp.odds !== null && opp.odds !== undefined 
                                                    ? formatDecimalOdds(opp.odds) 
                                                    : opp.is_dfs ? 'DFS' : 'N/A'}
                                            </td>
                                            <td>
                                                {opp.value_score !== null && opp.value_score !== undefined
                                                    ? formatValueScore(opp.value_score)
                                                    : opp.dfs_value_score !== null && opp.dfs_value_score !== undefined
                                                    ? `DFS: ${formatValueScore(opp.dfs_value_score)}`
                                                    : 'N/A'}
                                            </td>
                                            <td>
                                                {opp.line_advantage !== null && opp.line_advantage !== undefined
                                                    ? `${opp.line_advantage > 0 ? '+' : ''}${opp.line_advantage.toFixed(1)}`
                                                    : 'N/A'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    ) : (
                        <div className={componentStyles.tableEmpty}>
                            No additional opportunities available
                        </div>
                    )}
                </div>
            )}
            
        </div>
    );
};

export default SnipedOddsCard;
